import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { SkipEvent } from '../domain/SkipEvent';
import { SkipsProcessingJob } from '../domain/SkipsProcessingJob';

interface SkipEventTableProps {
  skipEvent: SkipEvent;
  validation: {
    isValid: boolean;
    errors: {
      archived: string[];
      continuation: string[];
    };
  };
  onFieldChange: (job: SkipsProcessingJob, field: string, value: any) => void;
}

export const SkipEventTable: React.FC<SkipEventTableProps> = ({ skipEvent, validation, onFieldChange }) => {
  const theme = useTheme();
  const { archivedJob, continuationJob } = skipEvent;
  const hasBothJobs = archivedJob && continuationJob;

  const hasError = (job: SkipsProcessingJob, field: string) => {
    if (job === archivedJob) {
      return validation.errors.archived.includes(field);
    }

    if (job === continuationJob) {
      return validation.errors.continuation.includes(field);
    }

    return false;
  };

  const renderTextField = (
    job: SkipsProcessingJob,
    field: string,
    value: string | number,
    type: 'number' | 'text' | 'textarea' = 'number',
  ) => {
    return (
      <TextField
        value={value}
        onChange={(e) => {
          const newValue = type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
          onFieldChange(job, field, newValue);
        }}
        size="small"
        fullWidth
        type={type === 'textarea' ? 'text' : type}
        multiline={type === 'textarea'}
        rows={type === 'textarea' ? 3 : undefined}
        error={hasError(job, field)}
        inputProps={
          type === 'number'
            ? {
                step: field.includes('Percent') ? 1 : 1,
                min: 0,
              }
            : {}
        }
      />
    );
  };

  const renderValue = (
    value: string | number | string[],
    job: SkipsProcessingJob | null = null,
    field: string = '',
  ) => {
    const hasErrorFlag = job && field ? hasError(job, field) : false;
    const textColor = hasErrorFlag ? theme.palette.error.main : 'inherit';
    const fontWeight = hasErrorFlag ? 'bold' : 'normal';

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <Typography variant="body2" sx={{ color: textColor, fontWeight }}>
            -
          </Typography>
        );
      }

      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map(
            (item, idx) => item && <Chip key={idx} label={item} size="small" sx={{ color: textColor, fontWeight }} />,
          )}
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ color: textColor, fontWeight }}>
        {value || '-'}
      </Typography>
    );
  };

  const renderShiftCell = (job: SkipsProcessingJob | null) => {
    if (!job) {
      return null;
    }

    return renderValue(job.shiftName, job, 'shift');
  };

  const headerBgColor = (jobType: 'archived' | 'continuation' | 'single') => {
    if (jobType === 'archived') {
      return '#ffe6e6';
    }

    if (jobType === 'continuation') {
      return '#e6ffe6';
    }

    return '#f5f5f5';
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Property</TableCell>
            {archivedJob && (
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: headerBgColor(archivedJob.isArchivedJob ? 'archived' : 'single'),
                  width: hasBothJobs ? '37.5%' : '75%',
                }}
              >
                {archivedJob.isArchivedJob ? 'Archived Job' : 'Job'}
              </TableCell>
            )}
            {continuationJob && (
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: headerBgColor('continuation'),
                  width: '37.5%',
                }}
              >
                Continuation Job
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Rogo Job ID */}
          <TableRow>
            <TableCell>Rogo Job ID</TableCell>
            {archivedJob && <TableCell>{renderValue(archivedJob.rogoJobId)}</TableCell>}
            {continuationJob && <TableCell>{renderValue(continuationJob.rogoJobId)}</TableCell>}
          </TableRow>

          {/* Name */}
          <TableRow>
            <TableCell>Name</TableCell>
            {archivedJob && <TableCell>{renderValue(archivedJob.name)}</TableCell>}
            {continuationJob && <TableCell>{renderValue(continuationJob.name)}</TableCell>}
          </TableRow>

          {/* Shift */}
          <TableRow>
            <TableCell>Shift</TableCell>
            {archivedJob && <TableCell>{renderShiftCell(archivedJob)}</TableCell>}
            {continuationJob && <TableCell>{renderShiftCell(continuationJob)}</TableCell>}
          </TableRow>

          {/* Invoice # */}
          <TableRow>
            <TableCell>Invoice #</TableCell>
            {archivedJob && (
              <TableCell>{renderValue(archivedJob.invoiceNumber, archivedJob, 'invoiceNumber')}</TableCell>
            )}
            {continuationJob && (
              <TableCell>{renderValue(continuationJob.invoiceNumber, continuationJob, 'invoiceNumber')}</TableCell>
            )}
          </TableRow>

          {/* Archive Job Link Skip Mgmt */}
          <TableRow>
            <TableCell>Archive Job Link Skip Mgmt</TableCell>
            {archivedJob && <TableCell>{renderValue(archivedJob.archiveJobLink)}</TableCell>}
            {continuationJob && <TableCell>{renderValue(continuationJob.archiveJobLink)}</TableCell>}
          </TableRow>

          {/* Cont Job Link Skip Mgmt */}
          <TableRow>
            <TableCell>Cont Job Link Skip Mgmt</TableCell>
            {archivedJob && <TableCell>{renderValue(archivedJob.contJobLink)}</TableCell>}
            {continuationJob && <TableCell>{renderValue(continuationJob.contJobLink)}</TableCell>}
          </TableRow>

          {/* Deleted Sples in Exe? */}
          <TableRow>
            <TableCell>Deleted Sples in Exe? (Skip Mgmt)</TableCell>
            {archivedJob && <TableCell>{renderValue(archivedJob.deletedSamplesInExe)}</TableCell>}
            {continuationJob && <TableCell>{renderValue(continuationJob.deletedSamplesInExe)}</TableCell>}
          </TableRow>

          {/* Skips in Exe? */}
          <TableRow>
            <TableCell>Skips in Exe? (Skip Mgmt)</TableCell>
            {archivedJob && <TableCell>{renderValue(archivedJob.skipsInExe)}</TableCell>}
            {continuationJob && <TableCell>{renderValue(continuationJob.skipsInExe)}</TableCell>}
          </TableRow>

          {/* Job Flags */}
          <TableRow>
            <TableCell>Job Flags</TableCell>
            {archivedJob && <TableCell>{renderValue(archivedJob.jobFlags)}</TableCell>}
            {continuationJob && <TableCell>{renderValue(continuationJob.jobFlags)}</TableCell>}
          </TableRow>

          {/* Data Sent */}
          <TableRow>
            <TableCell>Data Sent</TableCell>
            {archivedJob && <TableCell>{renderValue(archivedJob.dataSent)}</TableCell>}
            {continuationJob && <TableCell>{renderValue(continuationJob.dataSent)}</TableCell>}
          </TableRow>

          {/* Billable Percent % - EDITABLE */}
          <TableRow>
            <TableCell>Billable Percent %</TableCell>
            {archivedJob && (
              <TableCell>{renderTextField(archivedJob, 'billablePercent', archivedJob.billablePercent)}</TableCell>
            )}
            {continuationJob && (
              <TableCell>
                {renderTextField(continuationJob, 'billablePercent', continuationJob.billablePercent)}
              </TableCell>
            )}
          </TableRow>

          {/* # Samples - EDITABLE */}
          <TableRow>
            <TableCell># Samples</TableCell>
            {archivedJob && <TableCell>{renderTextField(archivedJob, 'numSamples', archivedJob.numSamples)}</TableCell>}
            {continuationJob && (
              <TableCell>{renderTextField(continuationJob, 'numSamples', continuationJob.numSamples)}</TableCell>
            )}
          </TableRow>

          {/* Exe # Samples - EDITABLE */}
          <TableRow>
            <TableCell>Exe # Samples</TableCell>
            {archivedJob && (
              <TableCell>{renderTextField(archivedJob, 'exeNumSamples', archivedJob.exeNumSamples)}</TableCell>
            )}
            {continuationJob && (
              <TableCell>{renderTextField(continuationJob, 'exeNumSamples', continuationJob.exeNumSamples)}</TableCell>
            )}
          </TableRow>

          {/* Exe # Samples Skipped - EDITABLE */}
          <TableRow>
            <TableCell>Exe # Samples Skipped</TableCell>
            {archivedJob && (
              <TableCell>
                {renderTextField(archivedJob, 'exeNumSamplesSkipped', archivedJob.exeNumSamplesSkipped)}
              </TableCell>
            )}
            {continuationJob && (
              <TableCell>
                {renderTextField(continuationJob, 'exeNumSamplesSkipped', continuationJob.exeNumSamplesSkipped)}
              </TableCell>
            )}
          </TableRow>

          {/* Previously Sampled Exe # - EDITABLE */}
          <TableRow>
            <TableCell>Previously Sampled Exe # - Skip Mgmt</TableCell>
            {archivedJob && (
              <TableCell>
                {renderTextField(archivedJob, 'previouslySampledExeNum', archivedJob.previouslySampledExeNum)}
              </TableCell>
            )}
            {continuationJob && (
              <TableCell>
                {renderTextField(continuationJob, 'previouslySampledExeNum', continuationJob.previouslySampledExeNum)}
              </TableCell>
            )}
          </TableRow>

          {/* Mgr Notes #ops - EDITABLE */}
          <TableRow>
            <TableCell>Mgr Notes #ops</TableCell>
            {archivedJob && (
              <TableCell>{renderTextField(archivedJob, 'mgrNotesOps', archivedJob.mgrNotesOps, 'textarea')}</TableCell>
            )}
            {continuationJob && (
              <TableCell>
                {renderTextField(continuationJob, 'mgrNotesOps', continuationJob.mgrNotesOps, 'textarea')}
              </TableCell>
            )}
          </TableRow>

          {/* Pulled Percent % - CALCULATED */}
          <TableRow>
            <TableCell>Pulled Percent % (calculated)</TableCell>
            {archivedJob && (
              <TableCell>
                <Box sx={{ p: 1, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {archivedJob.pulledPercent.toFixed(2)}%
                  </Typography>
                </Box>
              </TableCell>
            )}
            {continuationJob && (
              <TableCell>
                <Box sx={{ p: 1, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {continuationJob.pulledPercent.toFixed(2)}%
                  </Typography>
                </Box>
              </TableCell>
            )}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
